import {
  CheckCircle2,
  Circle,
  Flag,
  Lock,
  Play,
  RotateCcw,
  Sparkles,
  ThumbsUp,
  TimerReset,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { getDailyGuidance } from "@/lib/karma/daily";
import { buildCoachPlan, getLocalDateKey } from "@/lib/karma/missions";
import type {
  KarmaCoachEvidence,
  KarmaCoachGoal,
  KarmaUser,
  KarmaNumerology,
} from "@/lib/karma/types";
import { computeCoachTodaySummary } from "@/lib/karma/coach";
import {
  ensureStore,
  getMyCircle,
  getWitnessRequestForMission,
  giveKudosToWitnessRequest,
  listCircleMembers,
  listPendingWitnessQueue,
  reportWitnessRequest,
  resetCoachDay,
  setCoachClosingAnswer,
  setCoachEvidence,
  skipCoachMission,
  submitWitnessRequest,
  toggleCoachMissionCompleted,
  updateUser,
  verifyWitnessRequest,
} from "@/lib/karma/storage";

function goalLabel(goal: KarmaCoachGoal) {
  switch (goal) {
    case "iliski":
      return "İlişki";
    case "para":
      return "Para";
    case "saglik":
      return "Sağlık";
    case "uretim":
      return "Üretim";
    case "denge":
    default:
      return "Denge";
  }
}

function inferGoal(me: KarmaUser): KarmaCoachGoal {
  const g = me.coach?.goal;
  if (g) return g;
  if (me.intent && me.intent !== "belirsiz") return "iliski";
  return "denge";
}

function missionLabel(missionId: string) {
  if (missionId === "core-main") return "Ana eylem";
  if (missionId === "core-closing") return "Kapanış";
  if (missionId === "core-starter") return "Başlatıcı";
  return missionId;
}

function weekKey(date: Date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  const year = d.getFullYear();
  const start = new Date(year, 0, 1, 12, 0, 0, 0);
  const week = Math.ceil(((+d - +start) / 86400000 + start.getDay() + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

function isWithinWeek(dateKey: string, wk: string) {
  const [y, m, d] = dateKey.split("-").map((x) => Number(x));
  const date = new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
  return weekKey(date) === wk;
}

function useCountdownTimer(args: { onFinish: (missionId: string) => void }) {
  const { onFinish } = args;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number>(0);
  const intervalRef = useRef<number | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const stop = () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    activeIdRef.current = null;
    setActiveId(null);
    setRemaining(0);
  };

  const start = (id: string, seconds: number) => {
    if (!seconds) return;
    if (intervalRef.current) window.clearInterval(intervalRef.current);

    activeIdRef.current = id;
    setActiveId(id);
    setRemaining(seconds);

    intervalRef.current = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          const finishedId = activeIdRef.current;
          stop();
          if (finishedId) onFinishRef.current(finishedId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, []);

  return { activeId, remaining, start, stop };
}

function formatSeconds(s: number) {
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm <= 0) return `${ss}s`;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

function getCompletedSet(me: KarmaUser, dateKey: string) {
  const ids = me.coach?.completedByDate?.[dateKey] ?? [];
  return new Set(ids);
}

function getSkippedSet(me: KarmaUser, dateKey: string) {
  const ids = me.coach?.skippedByDate?.[dateKey] ?? [];
  return new Set(ids);
}

function getEvidence(me: KarmaUser, dateKey: string, missionId: string): KarmaCoachEvidence {
  return (me.coach?.evidenceByDate?.[dateKey]?.[missionId] ?? {}) as KarmaCoachEvidence;
}

export function SpiritualCoach(props: {
  me: KarmaUser;
  numerology: KarmaNumerology;
  onStoreChange: () => void;
}) {
  const { me, numerology, onStoreChange } = props;

  const daily = useMemo(() => getDailyGuidance(new Date()), []);
  const dateKey = useMemo(() => getLocalDateKey(new Date()), []);
  const goal = useMemo(() => inferGoal(me), [me]);

  const plan = useMemo(
    () => buildCoachPlan({ daily, numerology, goal }),
    [daily, numerology, goal],
  );

  const completed = getCompletedSet(me, dateKey);
  const skipped = getSkippedSet(me, dateKey);
  const skipUsed = skipped.size >= 1;

  const coreMissions = plan.missions.filter((m) => m.core);
  const bonusMissions = plan.missions.filter((m) => !m.core);

  const coreDone = coreMissions.filter((m) => completed.has(m.id)).length;
  const coreProgress = Math.round((coreDone / Math.max(1, coreMissions.length)) * 100);
  const rewardUnlocked = coreDone >= coreMissions.length;

  const stats = useMemo(() => computeCoachTodaySummary(me, new Date()), [me]);

  const { activeId, remaining, start, stop } = useCountdownTimer({
    onFinish: (missionId) => {
      setCoachEvidence(dateKey, missionId, { checks: ["__timer_done"] });
      onStoreChange();
    },
  });

  const closingText = me.coach?.notesByDate?.[dateKey]?.closingAnswer ?? "";

  const store = ensureStore();
  const myCircle = getMyCircle(store);
  const circleMembers = myCircle ? listCircleMembers(myCircle.id, store) : [];
  const witnessQueue = myCircle
    ? listPendingWitnessQueue(myCircle.id, store).filter((r) => r.fromUserId !== me.id)
    : [];

  const wk = weekKey(new Date());
  const raidTarget = 30;
  const raidProgress = myCircle
    ? store.witnessRequests
        .filter((r) => r.circleId === myCircle.id)
        .filter((r) => r.status === "verified")
        .filter((r) => isWithinWeek(r.dateKey, wk))
        .filter((r) => r.missionId === "core-main" || r.missionId === "core-closing")
        .length
    : 0;

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-4 w-4" /> Koç Modu
            </div>
            <div className="mt-2 font-display text-2xl font-semibold tracking-tight">
              {plan.themeTitle}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">{plan.themeOneLiner}</div>
            <div className="mt-3 text-sm text-muted-foreground">
              3 dakikalık akış: <span className="font-medium">Başla</span> →{" "}
              <span className="font-medium">Eylem</span> →{" "}
              <span className="font-medium">Kapanış</span>. Bonuslar isteğe bağlı.
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 rounded-2xl border bg-muted/30 px-4 py-3">
              <div className="grid gap-1">
                <div className="text-sm font-medium">Eşleşme</div>
                <div className="text-xs text-muted-foreground">
                  {me.matchEnabled ? "Açık" : "Kapalı (Koç Modu)"}
                </div>
              </div>
              <Switch
                checked={me.matchEnabled}
                onCheckedChange={(checked) => {
                  updateUser({ ...me, matchEnabled: checked });
                  onStoreChange();
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/karma/me">Panel</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/karma/cember">Çember</Link>
              </Button>
              <Button
                variant="secondary"
                className="rounded-full"
                onClick={() => {
                  stop();
                  resetCoachDay(dateKey);
                  onStoreChange();
                }}
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Bugünü sıfırla
              </Button>
            </div>
          </div>
        </div>

        <Separator className="my-5" />

        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <div className="text-sm font-semibold">Bugünün ilerlemesi</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Ana görevler: {coreDone}/{coreMissions.length} • Atla hakkı: {skipUsed ? "0/1" : "1/1"}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Seviye {stats.level.level}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                Bugün {stats.todayXp} XP
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                Seri {stats.streak}
              </span>
            </div>
            <div className="mt-3">
              <Progress value={coreProgress} />
            </div>
          </div>

          <Card className="p-4">
            {rewardUnlocked ? (
              <>
                <div className="text-sm font-semibold">Bugünün içgörüsü açıldı</div>
                <div className="mt-2 grid gap-2 text-sm">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Ders</div>
                    <div>{plan.reward.lesson}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Gölge tetikleyici</div>
                    <div>{plan.reward.shadowTrigger}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Yarın tek odak</div>
                    <div>{plan.reward.tomorrowFocus}</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Lock className="h-4 w-4" /> Bugünün içgörüsü kilitli
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  İçgörüyü açmak için 3 ana görevi tamamla.
                </div>
              </>
            )}
          </Card>
        </div>
      </Card>

      {stats.titles.length ? (
        <Card className="p-5">
          <div className="text-sm font-semibold">Unvanlar</div>
          <div className="mt-1 text-sm text-muted-foreground">Kaliteye bağlı olarak açılır.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {stats.titles.map((t) => (
              <span
                key={t.id}
                className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                title={t.description}
              >
                {t.name}
              </span>
            ))}
          </div>
        </Card>
      ) : null}

      {myCircle ? (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4" /> {myCircle.name}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {circleMembers.length} kişi • Haftalık raid: {wk}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                İtibar: {me.reputation ?? 0}
              </span>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/karma/cember">Yönet</Link>
              </Button>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <div className="font-semibold">Raid ilerlemesi</div>
              <div className="text-muted-foreground">
                {raidProgress}/{raidTarget} onay
              </div>
            </div>
            <div className="mt-2">
              <Progress value={Math.round((raidProgress / Math.max(1, raidTarget)) * 100)} />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Bu hafta çember içinde onaylanan (verified) kapanış/ana eylem sayısı.
            </div>
          </div>

          <Separator className="my-5" />

          <div className="text-sm font-semibold">Bugün teyit bekleyenler</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Negatif oy yok. Sadece teyit/teşekkür ve şüpheliyse rapor.
          </div>

          {witnessQueue.length ? (
            <div className="mt-4 grid gap-3">
              {witnessQueue.slice(0, 6).map((r) => {
                const user = store.users.find((u) => u.id === r.fromUserId);
                return (
                  <div key={r.id} className="rounded-2xl border bg-muted/30 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {user?.displayName ?? "Üye"} • {missionLabel(r.missionId)}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {r.dateKey} • {r.verifierUserIds.length}/{r.requiredCount} teyit
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          className="rounded-full"
                          onClick={() => {
                            verifyWitnessRequest(r.id);
                            onStoreChange();
                          }}
                        >
                          Teyit et
                        </Button>
                        <Button
                          className="rounded-full"
                          variant="secondary"
                          onClick={() => {
                            giveKudosToWitnessRequest(r.id);
                            onStoreChange();
                          }}
                        >
                          <ThumbsUp className="mr-2 h-4 w-4" /> Teşekkür
                        </Button>
                        <Button
                          className="rounded-full"
                          variant="outline"
                          onClick={() => {
                            reportWitnessRequest(r.id);
                            onStoreChange();
                          }}
                        >
                          <Flag className="mr-2 h-4 w-4" /> Raporla
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-4 text-sm text-muted-foreground">Şu an bekleyen teyit yok.</div>
          )}
        </Card>
      ) : (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4" /> Çember yok
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Teyit sistemi için bir çembere katılman gerekir.
              </div>
            </div>
            <Button asChild className="rounded-full">
              <Link to="/karma/cember">Çember seç</Link>
            </Button>
          </div>
        </Card>
      )}

      <section className="grid gap-4">
        <div className="font-display text-lg font-semibold tracking-tight">Ana görevler</div>

        {coreMissions.map((m) => {
          const done = completed.has(m.id);
          const isSkipped = skipped.has(m.id);
          const effective = isSkipped && m.miniAlternative ? m.miniAlternative : m;
          const running = activeId === m.id;
          const evidence = getEvidence(me, dateKey, m.id);

          const canSkip = !skipUsed && !done && !isSkipped;
          const skipLabel = isSkipped ? "Atlandı (mini)" : "Atla";

          return (
            <Card key={m.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="font-display text-lg font-semibold tracking-tight">
                      {m.title}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">{effective.description}</div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-xs text-muted-foreground">Süre</div>
                  <div className="text-sm font-medium">
                    {running ? formatSeconds(remaining) : formatSeconds(effective.seconds)}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  className="rounded-full"
                  variant="outline"
                  onClick={() => {
                    if (running) stop();
                    else start(m.id, effective.seconds);
                  }}
                >
                  {running ? (
                    <>
                      <TimerReset className="mr-2 h-4 w-4" /> Dur
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" /> Başla
                    </>
                  )}
                </Button>

                <Button
                  className="rounded-full"
                  variant={done ? "secondary" : "default"}
                  onClick={() => {
                    toggleCoachMissionCompleted(m.id, dateKey);
                    stop();
                    onStoreChange();
                  }}
                >
                  {done ? "Geri al" : "Tamamlandı"}
                </Button>

                <Button
                  className="rounded-full"
                  variant="outline"
                  disabled={!canSkip}
                  onClick={() => {
                    skipCoachMission(m.id, dateKey);
                    stop();
                    onStoreChange();
                  }}
                  title={canSkip ? "Bugün sadece 1 atlama hakkın var" : "Atlama hakkın yok"}
                >
                  {skipLabel}
                </Button>
              </div>

              {myCircle && (m.id === "core-main" || m.id === "core-closing") ? (() => {
                const req = getWitnessRequestForMission({
                  fromUserId: me.id,
                  dateKey,
                  missionId: m.id,
                  store,
                });
                const status = req?.status;

                const submitted = status === "pending" || status === "verified";
                const canSubmit = done && !submitted;

                return (
                  <div className="mt-4 rounded-2xl border bg-muted/30 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold">Topluluk onayı (Witness)</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {status === "verified"
                            ? "Onaylandı (verified) — bonus XP ve itibar"
                            : status === "pending"
                              ? "Onay bekliyor"
                              : "İstersen onaya gönder"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          className="rounded-full"
                          disabled={!canSubmit}
                          onClick={() => {
                            submitWitnessRequest(dateKey, m.id);
                            onStoreChange();
                          }}
                        >
                          Onaya gönder
                        </Button>
                        {status === "pending" ? (
                          <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                            {req?.verifierUserIds.length ?? 0}/{req?.requiredCount ?? 2}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Not: Kimlerin teyit ettiği kullanıcıya gösterilmez.
                    </div>
                  </div>
                );
              })() : null}

              {m.evidence.kind === "checks" && m.evidence.options?.length ? (
                <div className="mt-4 rounded-2xl border bg-muted/30 p-4">
                  <div className="text-sm font-semibold">Kanıt</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.evidence.options.map((opt) => {
                      const selected = (evidence.checks ?? [])[0] === opt;
                      return (
                        <Button
                          key={opt}
                          type="button"
                          className="rounded-full"
                          variant={selected ? "default" : "outline"}
                          onClick={() => {
                            setCoachEvidence(dateKey, m.id, { checks: [opt] });
                            onStoreChange();
                          }}
                        >
                          {opt}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {m.evidence.kind === "text" ? (
                <div className="mt-4 rounded-2xl border bg-muted/30 p-4">
                  <div className="text-sm font-semibold">Kapanış notu</div>
                  <div className="mt-1 text-sm text-muted-foreground">{m.evidence.prompt}</div>
                  <Textarea
                    className="mt-3"
                    rows={3}
                    value={closingText}
                    onChange={(e) => {
                      setCoachClosingAnswer(dateKey, e.target.value);
                      onStoreChange();
                    }}
                    placeholder="Tek cümle yeter..."
                  />
                </div>
              ) : null}
            </Card>
          );
        })}
      </section>

      <section className="grid gap-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">Bonuslar</div>
            <div className="mt-1 text-sm text-muted-foreground">
              İstersen ekle. Ama ana akış 3 görev.
            </div>
          </div>
          <div className="text-xs text-muted-foreground">Odak: {goalLabel(goal)}</div>
        </div>

        {bonusMissions.map((m) => {
          const done = completed.has(m.id);
          const isSkipped = skipped.has(m.id);
          const effective = isSkipped && m.miniAlternative ? m.miniAlternative : m;
          const running = activeId === m.id;
          const canSkip = !skipUsed && !done && !isSkipped;

          return (
            <Card key={m.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="font-display text-lg font-semibold tracking-tight">
                      {m.title}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">{effective.description}</div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-xs text-muted-foreground">Süre</div>
                  <div className="text-sm font-medium">
                    {running ? formatSeconds(remaining) : formatSeconds(effective.seconds)}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  className="rounded-full"
                  variant="outline"
                  onClick={() => {
                    if (running) stop();
                    else start(m.id, effective.seconds);
                  }}
                >
                  {running ? (
                    <>
                      <TimerReset className="mr-2 h-4 w-4" /> Dur
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" /> Başla
                    </>
                  )}
                </Button>

                <Button
                  className="rounded-full"
                  variant={done ? "secondary" : "default"}
                  onClick={() => {
                    toggleCoachMissionCompleted(m.id, dateKey);
                    stop();
                    onStoreChange();
                  }}
                >
                  {done ? "Geri al" : "Tamamlandı"}
                </Button>

                <Button
                  className="rounded-full"
                  variant="outline"
                  disabled={!canSkip}
                  onClick={() => {
                    skipCoachMission(m.id, dateKey);
                    stop();
                    onStoreChange();
                  }}
                >
                  {isSkipped ? "Atlandı (mini)" : "Atla"}
                </Button>
              </div>
            </Card>
          );
        })}
      </section>

      <Card className="p-5">
        <div className="text-sm font-semibold">Koç notu</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Bugün {goalLabel(goal)} odağında ilerlemek için tek bir şeyi seç: “Bugün {goalLabel(goal)} için
          yapacağım şey …”.
        </div>
      </Card>
    </div>
  );
}
